import { useEffect, useState, useCallback } from 'react';
import { Box, Modal, Tooltip, TextField, withStyles, InterestAccountTransferRequest, Label } from 'vopay';
import styles from './InterestAccountFundTransferModal.module.scss';
import usePostInterestAccountTransfer from '@/hooks/useApi/usePostInterestAccountTransfer';
import InterestAccountFundTransferModalError from '../InterestAccountFundTransferModalError/InterestAccountFundTransferModalError';
import { Title, Card, CircleButton, Button } from 'UI';
import { useTranslation } from 'react-i18next';

type TransferType = 'Fund' | 'Withdraw';

type AddInterestAccountProps = {
  visible: boolean;
  onClose: (added: boolean) => void;
  transferType: TransferType;
  currentBalance: number;
  currency: string;
  formatAmount: (amount: string) => string;
};

const cn = withStyles(styles);

const InterestAccountFundTransferModal: React.FC<AddInterestAccountProps> = ({
  visible,
  onClose,
  transferType,
  currentBalance,
  currency,
  formatAmount,
}) => {
  const { t } = useTranslation('translations', {
    keyPrefix: 'Accounts.AccountBalances.InterestAccountFundTransferModal',
  });
  const [fundAmount, setFundAmount] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [hasErrors, setHasErrors] = useState(false);
  const [responseErrorMessage, setResponseErrorMessage] = useState('');
  const [updatedBalance, setUpdatedBalance] = useState(currentBalance);

  const { postInterestAccountTransfer } = usePostInterestAccountTransfer();

  const transferTypeMap = {
    Fund: {
      title: t('Fund.title', { defaultValue: 'Add Funds' }),
      label: t('Fund.label', { defaultValue: 'Add Funds' }),
      buttonText: t('Fund.buttonText', { defaultValue: 'Add Funds' }),
      tooltip: t('Fund.tooltip', {
        defaultValue:
          'The amount you put here will be transferred from your short term interest account to the long term interest account.',
      }),
    },
    Withdraw: {
      title: t('Withdraw.title', { defaultValue: 'Withdraw Funds' }),
      label: t('Withdraw.label', { defaultValue: 'Withdraw Funds' }),
      buttonText: t('Withdraw.buttonText', { defaultValue: 'Withdraw Funds' }),
      tooltip: t('Withdraw.tooltip', {
        defaultValue: 'The amount will be transferred to the Short Term Interest Account.',
      }),
    },
  };

  const { title, label, buttonText, tooltip } = transferTypeMap[transferType];

  async function createTransaction() {
    const InterestAccountTransferParams: InterestAccountTransferRequest = {
      PaymentType: transferType,
      Amount: fundAmount.replace(/,/g, ''),
    };

    const postInterestAccountTransferResponse = await postInterestAccountTransfer(InterestAccountTransferParams);

    if (!postInterestAccountTransferResponse.Success) {
      setErrorMessage(t('accountCreationError', { defaultValue: 'Account creation failed for the following reason' }));
      setHasErrors(true);
      setResponseErrorMessage(postInterestAccountTransferResponse.ErrorMessage);
    } else {
      setFundAmount('');
      onClose(true);
    }
  }

  useEffect(() => {
    setUpdatedBalance(currentBalance);
  }, [currentBalance]);

  const updateBalance = useCallback(
    (amount: string) => {
      if (amount === '') {
        setUpdatedBalance(currentBalance);
      } else if (!Number.isNaN(parseFloat(amount))) {
        if (typeof amount === 'string') {
          setUpdatedBalance(currentBalance - parseFloat(amount.replace(/,/g, '')));
        }
      }
    },
    [currentBalance],
  );

  const handleFundAmountChange = (amount: string) => {
    setFundAmount(amount);
    updateBalance(amount);
  };

  return (
    <>
      <Modal isVisible={visible} enableBgClick={false}>
        <Card className={cn('interestAccountFundTransferModalContainer')}>
          <Title level="3">{title}</Title>

          <Box className={cn('addFundsCloseButton')}>
            <CircleButton
              icon="close"
              size="large"
              onClick={() => {
                setFundAmount('');
                onClose(false);
              }}
            />
          </Box>

          <div className={cn('addFundsCard')}>
            {transferType === 'Fund'
              ? t('availableStandardBalance', { defaultValue: 'Available Short Term Balance' })
              : t('availablePremiumBalance', { defaultValue: 'Available Premium Balance' })}

            <Title level="2">
              {currency} {formatAmount(updatedBalance.toFixed(2))}
            </Title>
          </div>

          <div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '2px',
                marginBottom: '8px',
              }}
            >
              <Label htmlFor="interestAccountFundAmount" label={label} required isBold={false} padding="0" />

              <Tooltip
                borderColor="rgba(45, 45, 45, 0.6)"
                icon="info"
                position="top"
                bgColor="#fbfbfb"
                information={tooltip}
                popoverClassName={cn('tooltip')}
              />
            </div>

            <TextField
              id="interestAccountFundAmount"
              variant="outlineShadow"
              inputWidth="100%"
              containerWidth="100%"
              preset="currency"
              key="AddInterestAccountTransferAmount"
              name="AddInterestAccountTransferAmount"
              error={errorMessage}
              placeholder={t('placeholder', { defaultValue: 'Enter Amount' })}
              value={fundAmount}
              onBlur={(e) => {
                validateAmount(e.target.value);
                handleFundAmountChange(e.target.value);
              }}
              onChange={(e) => {
                setFundAmount(e.target.value);
              }}
            />
          </div>

          <Button
            colorScheme="primary"
            type="button"
            disabled={errorMessage !== '' || parseFloat(fundAmount) === 0 || fundAmount === ''}
            onClick={() => {
              createTransaction();
            }}
          >
            {buttonText}
          </Button>
        </Card>
      </Modal>

      <InterestAccountFundTransferModalError
        failureReason={t('transactionErrorTitle', { defaultValue: 'Transaction failed for the following reason' })}
        errorMessage={responseErrorMessage ?? ''}
        visible={hasErrors}
        onClose={() => setHasErrors(false)}
      />
    </>
  );

  function validateAmount(val: string) {
    if (val === '') {
      setErrorMessage('');
      return;
    }

    if (!/^\d{1,3}(,\d{3})*(\.\d+)?$/.test(val) || parseFloat(val.replace(/,/g, '')) < 0) {
      setErrorMessage(t('invalidNumber', { defaultValue: 'Invalid number. Please try again.' }));
    } else if (parseFloat(val) > currentBalance) {
      setErrorMessage(t('amountExceedsBalance', { defaultValue: 'Amount cannot exceed the available short term balance.' }));
    } else {
      setErrorMessage('');
    }
  }
};

export default InterestAccountFundTransferModal;
